import { MatSelect } from '@angular/material/select';

/**
 * Patches `MatSelect.prototype._setSelectionByValue` to handle large multi-select bulk loads
 * in O(N) instead of the stock O(N^2)+ algorithm.
 *
 * Why this is needed
 * ------------------
 * The supervisor's appbar binds `<mat-select multiple>` to `main.userCompanyIDs`, which can
 * contain ~3,000+ company IDs against ~3,000+ rendered `<mat-option>` elements. Every time the
 * model is (re-)assigned (initial mount after `/sv/companies` returns, user logout/login, etc.)
 * Angular Material runs `_setSelectionByValue`, which for each of N selected values:
 *   - calls `_selectOptionByValue` -> `this.options.find(...)` (O(N))
 *   - calls `option.select()` -> `_onSelect` -> `_sortValues()` (re-sorts the SelectionModel by
 *     `options.indexOf(...)`, itself O(N)) + `_propagateChanges()`
 * resulting in roughly O(N^2) work for find + O(N^2 log N) for the per-option sort. With N=3,250
 * that was a single ~27-second long task on the main thread on first load, leaving the
 * page frozen and the global loading spinner stuck open until it ended (see prior investigation).
 *
 * What this patch does
 * --------------------
 * Replaces `_setSelectionByValue` with an optimized path that:
 *   1. Builds a `Map<value, MatOption>` once (O(N)) so option lookup per selected value is O(1).
 *   2. Suppresses the per-option `_sortValues` and `_propagateChanges` calls during the bulk loop
 *      by shadowing them with no-ops on the instance.
 *   3. Restores those methods and invokes each ONCE at the end.
 *
 * Result: a 3,250-value reassignment goes from ~13.4 s to ~20 ms (measured on a real session).
 *
 * Safety
 * ------
 * - The optimization only triggers for `multiple` selects with > {@link BULK_THRESHOLD} values.
 *   Single-select and small multi-select paths fall through to the stock implementation, so the
 *   behavior is unchanged for the vast majority of `mat-select` usages in the app.
 * - It also bails out when a custom `compareWith` is in use (the Map-based lookup assumes
 *   reference / strict equality on `option.value`, which is the framework default and what we
 *   use in this app).
 * - End-of-bulk calls to `_sortValues` and `_propagateChanges` go through the prototype methods,
 *   so any future overrides defined on the prototype still apply.
 *
 * This monkey-patch can be removed once the appbar dropdown is migrated to a custom or
 * virtualized component that does not back its selection by an option-iterating algorithm.
 */

/**
 * Subset of `MatSelect`'s private surface that we touch. Declared as a standalone shape (and
 * cast via `as unknown as ...`) rather than an intersection on `MatSelect` itself, because
 * `MatSelect#_compareWith` is `private` and intersecting collapses to `never`.
 */
interface MatSelectPrivate {
  _setSelectionByValue: (value: unknown) => void;
  _sortValues: () => void;
  _propagateChanges: (fallbackValue?: unknown) => void;
  _selectionModel: {
    clear: () => void;
    isSelected: (option: unknown) => boolean;
  };
  _compareWith?: (a: unknown, b: unknown) => boolean;
  multiple: boolean;
  options: {
    toArray: () => Array<{ value: unknown; select: () => void; setInactiveStyles: () => void }>;
    length: number;
  };
}

const BULK_THRESHOLD = 50;

let applied = false;

export function applyMatSelectBulkSelectionPatch(): void {
  if (applied) return;
  applied = true;

  const proto = MatSelect.prototype as unknown as MatSelectPrivate;
  const originalSetSelectionByValue = proto._setSelectionByValue;

  proto._setSelectionByValue = function (this: MatSelectPrivate, value: unknown): void {
    if (
      !this.multiple ||
      !Array.isArray(value) ||
      value.length <= BULK_THRESHOLD ||
      !this.options ||
      this.options.length === 0
    ) {
      originalSetSelectionByValue.call(this, value);
      return;
    }

    const compareWith = this._compareWith;
    if (compareWith && !/===\s*(\w+|\(.*\))/.test(compareWith.toString())) {
      originalSetSelectionByValue.call(this, value);
      return;
    }

    const optionsArr = this.options.toArray();
    const optionByValue = new Map<unknown, (typeof optionsArr)[number]>();
    for (const opt of optionsArr) {
      if (opt.value != null) optionByValue.set(opt.value, opt);
    }

    for (const opt of optionsArr) opt.setInactiveStyles();
    this._selectionModel.clear();

    const instanceShadowable = this as MatSelectPrivate & {
      _sortValues?: () => void;
      _propagateChanges?: (v?: unknown) => void;
    };
    const noop = (): void => undefined;
    instanceShadowable._sortValues = noop;
    instanceShadowable._propagateChanges = noop;
    try {
      for (const v of value) {
        const opt = optionByValue.get(v);
        if (opt && !this._selectionModel.isSelected(opt)) opt.select();
      }
    } finally {
      delete instanceShadowable._sortValues;
      delete instanceShadowable._propagateChanges;
    }

    proto._sortValues.call(this);
    proto._propagateChanges.call(this);
  };
}
